import { useMutation } from '@apollo/client';
import type { UpdateTaxonomyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateTaxonomyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type UpdateTaxonomyInput,
  useUpdateTaxonomyTRPC,
} from './useUpdateTaxonomyTRPC';

/**
 * Flag-gated wrapper for the optimistic-concurrency `updateTaxonomy` mutation
 * (RSP-6488). Both paths return the GraphQL `{ update_taxonomy: { affected_rows
 * } }` shape; a concurrent edit yields `affected_rows: 0` (the tRPC twin maps a
 * CONFLICT to it), so the consumer's existing "updated by another user" branch
 * fires identically on either path. Consumers import THIS hook.
 */
export const useUpdateTaxonomy = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(UpdateTaxonomyDocument, {
    update: (cache) => {
      evictField(cache, 'taxonomy');
      evictField(cache, 'taxonomy_audit');
    },
  });

  const trpcMutation = useUpdateTaxonomyTRPC();

  const updateTaxonomy = async (
    input: UpdateTaxonomyInput
  ): Promise<UpdateTaxonomyMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateTaxonomy(input);
    }

    const result = await updateGraphQL({
      variables: {
        Id: input.Id,
        Common: input.Common,
        Library: input.Library,
        Rating: input.Rating,
        Taxonomy: input.Taxonomy,
        InternalAuditRating: input.InternalAuditRating,
        OversightRating: input.OversightRating,
        // Threaded RAW — never reformatted.
        OriginalTimestamp: input.OriginalTimestamp,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update taxonomy');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateTaxonomy,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateTaxonomy,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
